/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useSyncExternalStore, type ReactNode } from 'react';

export type ContentWidth = 'narrow' | 'medium' | 'wide';

const STORAGE_KEY = 'sk_content_width';
const DEFAULT: ContentWidth = 'narrow';
const listeners = new Set<() => void>();

function getSnapshot(): ContentWidth {
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === 'narrow' || v === 'medium' || v === 'wide') return v;
  return DEFAULT;
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

const ContentWidthContext = createContext<{
  width: ContentWidth;
  setWidth: (w: ContentWidth) => void;
}>({ width: DEFAULT, setWidth: () => {} });

export function ContentWidthProvider({ children }: { children: ReactNode }) {
  const width = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT);

  const setWidth = useCallback((w: ContentWidth) => {
    localStorage.setItem(STORAGE_KEY, w);
    listeners.forEach((cb) => cb());
  }, []);

  return (
    <ContentWidthContext.Provider value={{ width, setWidth }}>
      {children}
    </ContentWidthContext.Provider>
  );
}

export function useContentWidth() {
  return useContext(ContentWidthContext);
}

export const WIDTH_CLASSES: Record<ContentWidth, string> = {
  narrow: 'max-w-2xl',
  medium: 'max-w-4xl',
  wide: 'max-w-6xl',
};
